/* eslint-disable filename-rules/match */
import { useCallback, useEffect, useMemo, useState } from 'react'

import styled from '@emotion/styled'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { PHASE_PRODUCTION_BUILD } from 'next/constants'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { Header } from '@cash-design-system/react'
import { spacing, StructuredContent } from '@cash-design-system/react'
// todo: fix once breakpoints exposed from arcade
// eslint-disable-next-line no-restricted-imports
import { BreakpointSmall } from '@cash-design-system/tokens'
import { useLogCashWebCdfEvent, useLogPageViewEventstream } from '@cash-web/shared-feature-analytics'
import { ContactUs, qrHashes, QrThemes } from '@cash-web/support/ui-components'
import { CustomerSupportAccessViewNodes, WebViewViewPage } from '@squareup/customer-data-events-web'

import { supportedRegions } from '../../data/regions'
import i18nextConfig, { fallbackLng as defaultLocale } from '../../next-i18next.config'
import { getArticle } from '../../src/api'
import ArticleList from '../../src/components/ArticleList'
import HelpContent from '../../src/components/helpContent'
import Search from '../../src/components/Search'
import StatusBadge from '../../src/components/StatusBadge'
import { getPublicRuntimeConfig } from '../../src/config/nextConfig'
import { useAnalyticsContext } from '../../src/contexts/Analytics'
import type { Article, ArticleListing } from '../../types'

const StyledArticle = styled.article`
  width: 100%;
`

// TODO can this be shared?
const StyledStructuredContent = styled(StructuredContent)`
  margin: 0;

  > * {
    max-width: 100%;
  }

  ${props => props.theme.typography.bodyText}
  line-height: 1.7;
  @media (min-width: ${BreakpointSmall}) {
    ${props => props.theme.typography.mainBody}
    line-height: 1.7;
  }

  p:first-of-type {
    margin-top: 0;
  }
  p:last-of-type {
    margin-bottom: 0;
  }

  img {
    max-height: 600px;
  }

  ol,
  ul {
    // These padding changes are so that numbered lists and bulleted
    // lists line up. We are using "em" rather than "rem" so we can
    // have the padding amount increased as the parent's font size increases
    // to leave enough room for the number

    padding-left: 1.5em;

    li {
      padding-left: 0.5em;
    }
  }

  ol {
    list-style: none;
    counter-reset: list;

    & > li {
      counter-increment: list;

      &:before {
        content: counter(list);
        position: relative;
        display: inline-block;

        // font size a bit smaller
        font-size: 0.75em;
        font-weight: ${props => props.theme.weights.bold};

        // creates a box around the number
        background: ${props => props.theme.colors.secondaryButtonBackground};
        border-radius: 33.3%;
        line-height: 1.5em;
        width: 1.5em;

        // Move the number box to left
        left: -1em;
        top: -0.125em;
        // Essentually removes the space left by relative pos
        margin-left: -1.5em;

        // align to center
        text-align: center;
      }
    }
  }
`

const { deepLinkPath, qrPath } = getPublicRuntimeConfig()

interface ArticleProps {
  article: Article
  childArticles?: ArticleListing[]
}

const Title = styled(Header)`
  margin-bottom: ${spacing.m};
`

const ContentContainer = styled.div`
  background: ${props => props.theme.colors.background};
`

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ locale = defaultLocale, params }) => {
  const articleSlug = params?.['article'] as string
  const articleId = articleSlug.match(/^(\d+)[a-zA-Z0-9-]*$/)?.[1]

  const region = params?.['region'] as string

  if (
    !region ||
    !articleId ||
    // @ts-expect-error If region as a string isn't a known country code this should fail
    !supportedRegions.includes(region)
  ) {
    return { notFound: true, revalidate: 3600 }
  }

  // Fetch article and translations
  return Promise.all([
    getArticle(articleId, { locale, region }),
    serverSideTranslations(locale, ['common'], i18nextConfig),
  ]).then(([{ article: responseArticle, child_articles: responseChildArticles }, translations]) => {
    if (!responseArticle) {
      return { notFound: true, revalidate: 3600 }
    }

    // Redirect if there's a redirect_token from the CMS
    if (responseArticle.redirect_token && articleId != responseArticle.redirect_token) {
      return {
        redirect: { permanent: false, destination: `/${region}/${responseArticle.redirect_token}` },
        revalidate: 3600,
      }
    }

    // TODO cameronandrews remove this when getRegion and getLocale are implemented in frontend service
    // TODO cameronandrews as getRegion and getLocale return hard coded 'us' and 'en-US' this effectively limits articles to just us and en-US
    if (
      locale.toLowerCase() !== responseArticle.locale?.toLowerCase() ||
      region != responseArticle.region?.toLowerCase() ||
      !responseArticle.token
    ) {
      return { notFound: true, revalidate: 3600 }
    }

    const slugifiedUrl = [articleId, slugify(responseArticle.title)].filter(text => text).join('-') // The slugified URL only includes the title if it is set

    // Redirect if the title isn't in the URL, except at build time which isn't supported
    if (articleSlug != slugifiedUrl && process.env['NEXT_PHASE'] !== PHASE_PRODUCTION_BUILD) {
      return { redirect: { permanent: false, destination: slugifiedUrl }, revalidate: 3600 }
    }

    const article: Article = {
      id: responseArticle.token,
      title: responseArticle.title ?? '',
      content: responseArticle.body ?? '',
      token: responseArticle.token ?? '', // TODO cameronandrews reduce duplication between ID and token
      metaDescription: responseArticle.meta_description,
    }

    const childArticles: ArticleListing[] | undefined = responseChildArticles?.map(childArticle => ({
      id: childArticle.token,
      title: childArticle.title,
      token: childArticle.token,
      content: childArticle.summary,
      slug: childArticle.slug,
      icon: 'documents',
    }))

    return {
      props: {
        article,
        childArticles,
        ...translations,
      },
      revalidate: 3600,
    }
  })
}

const DESKTOP_QR_SIZE = 300

const ArticlePage: NextPage<ArticleProps> = ({ article, childArticles }) => {
  const { viewToken } = useAnalyticsContext()
  const { t } = useTranslation()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const desktopQRSize = DESKTOP_QR_SIZE
  const launchChatDeepLink = deepLinkPath('support/chat?page=help')
  const logPageViewEvent = useLogPageViewEventstream()
  const logCdfEvent = useLogCashWebCdfEvent()

  const onHashChanged = () => {
    const hash = window.location.hash
    setOpen(hash === qrHashes['chat'])
  }

  const displayTypes = useMemo(() => childArticles?.map(() => 'node_tree'), [childArticles])

  const nodeTokens = useMemo(() => childArticles?.map(item => item.token), [childArticles])

  const logPageAnalytics = useCallback(() => {
    const totalCount = childArticles?.length || 0
    logCdfEvent(WebViewViewPage({ pageName: 'Support Article Page' }))
    logCdfEvent(
      CustomerSupportAccessViewNodes({
        displayTypes: displayTypes?.toString(),
        nodeTokens: nodeTokens?.toString(),
        parentToken: article.id,
        totalCount,
        trigger: 'NODE',
        viewToken: viewToken,
        searchText: '', // TODO capture if this came from a search text
      })
    )
    logPageViewEvent({
      page: article.title,
    })
  }, [childArticles, logCdfEvent, displayTypes, nodeTokens, article.id, article.title, viewToken, logPageViewEvent])

  useEffect(() => {
    // check on initial page load
    if (typeof window !== 'undefined') {
      logPageAnalytics()
      onHashChanged()
    }

    window?.addEventListener('hashchange', onHashChanged)

    return () => {
      window?.removeEventListener('hashchange', onHashChanged)
    }
  }, [logPageAnalytics])

  const closeQrModal = useCallback(() => {
    // Remove #chat from the URL
    router.replace(
      {
        hash: '',
        query: router.query,
      },
      router.asPath.replace('#chat', '') == '/' ? '/' : undefined, // Mask the URL
      { shallow: true }
    )
    setOpen(false)
  }, [router])

  return (
    <>
      <Head>
        <title>{t('Cash App - Support', { context: 'article page title' })}</title>
        <meta name="description" content={article.metaDescription} />
      </Head>
      <ContentContainer>
        <HelpContent>
          <section>
            <StatusBadge />
            <Search invert showMoreResults />
          </section>
          <section>
            <StyledArticle>
              <Title data-testid={article.id} variant="header-3" color="label" as="h2">
                {article.title}
              </Title>
              <StyledStructuredContent dangerouslySetInnerHTML={{ __html: article.content }}></StyledStructuredContent>
            </StyledArticle>
          </section>
        </HelpContent>
      </ContentContainer>
      <HelpContent>
        {childArticles?.length ? (
          <section>
            <ArticleList
              title={t('Related', {
                context: 'Subheading on the help article page with a list of articles related to the current article',
              })}
              data={childArticles}
              trigger="NODE"
              analyticsType="NODE_TREE"
            />
          </section>
        ) : undefined}
        <section>
          <ContactUs
            mobileChatDeepLink={launchChatDeepLink}
            desktopQRPath={qrPath('launch/support/chat')}
            desktopQRSize={desktopQRSize}
            desktopQRTheme={QrThemes.LIGHT}
            open={open}
            onCloseQrModal={closeQrModal}
            cdfTrigger={{
              viewToken: viewToken,
              trigger: 'NODE',
              triggerNodeToken: article.id,
            }}
          />
        </section>
      </HelpContent>
    </>
  )
}

/** Converts text into a slug */
export const slugify = (text?: string) =>
  text
    ?.replaceAll(/[^a-z0-9 ]/gi, '')
    .trim()
    .replaceAll(' ', '-')
    .toLowerCase() ?? ''

export default ArticlePage
